/**
 * DocumentLinksSection — section partagée Documents broker (Chunk 9.L.13).
 *
 * Surface UI nested sous ProjectDetailPage + SubmissionDetailPage : liste les
 * document_links scopés sur l'entité parent (`attachable_kind` = `project`
 * ou `submission`) + actions broker create/delete.
 *
 * Backend wire shapes (cf. apps/web/src/lib/queries/documentLinks.ts) :
 *   - list   : `{items: DocumentLinkRow[], next_cursor: string|null}`
 *   - create : full row post-insert
 *   - delete : `{id}` minimaliste (parité 9.L.7 callout C3)
 *
 * Hooks consommés selon `scope` prop :
 *   - `project`    → `useDocumentLinksByProject({projectId})`
 *   - `submission` → `useDocumentLinksBySubmission({submissionId})`
 *
 * Pattern parité strict 9.L.12 `MissingInformationSection` (state local,
 * dialogs interconnectés, microcopy hint, refetch via invalidation cache).
 *
 * Anti-patterns évités :
 *   - Pas de `<div onClick>` — boutons natifs.
 *   - i18n strict (pas de FR hardcodée).
 *   - Refetch via invalidation cache TanStack Query.
 */

import type { DocumentLinkRow } from "@kollectivia/shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AgentBadge } from "../../../components/AgentBadge";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  useDocumentLinksByProject,
  useDocumentLinksBySubmission,
} from "../../../lib/queries/documentLinks";
import { useFileDownloadUrl } from "../../../lib/queries/files";
import { toast } from "../../../lib/toast";

import { CreateDocumentLinkDialog } from "./CreateDocumentLinkDialog";
import { DeleteDocumentLinkDialog } from "./DeleteDocumentLinkDialog";

export type DocumentLinksSectionProps =
  | { scope: "project"; projectId: string }
  | { scope: "submission"; submissionId: string };

export function DocumentLinksSection(props: DocumentLinksSectionProps) {
  const { t } = useTranslation();

  const byProject = useDocumentLinksByProject({
    projectId: props.scope === "project" ? props.projectId : undefined,
    enabled: props.scope === "project",
  });
  const bySubmission = useDocumentLinksBySubmission({
    submissionId: props.scope === "submission" ? props.submissionId : undefined,
    enabled: props.scope === "submission",
  });
  const query = props.scope === "project" ? byProject : bySubmission;

  const [createOpen, setCreateOpen] = useState(false);
  const [deletingFor, setDeletingFor] = useState<DocumentLinkRow | null>(null);

  const items = useMemo<DocumentLinkRow[]>(() => query.data?.items ?? [], [query.data?.items]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
            {t("document_links.section.title")}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">{t("document_links.section.hint")}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:outline-brand-500 rounded-md px-3 py-1.5 text-xs font-medium text-white shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {t("document_links.section.create_button")}
        </button>
      </div>

      {items.length > 0 && (
        <p className="mb-3 text-xs text-slate-500">
          {t("document_links.section.count", { count: items.length })}
        </p>
      )}

      {query.isLoading ? (
        <p className="text-sm text-slate-400">{t("app.loading")}</p>
      ) : query.isError ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-red-700">{t("document_links.section.error")}</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="text-brand-700 focus-visible:outline-brand-500 self-start text-xs hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {t("app.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">{t("document_links.section.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-200">
          {items.map((dl) => (
            <DocumentLinkRowItem key={dl.id} dl={dl} onDelete={() => setDeletingFor(dl)} />
          ))}
        </ul>
      )}

      {createOpen &&
        (props.scope === "project" ? (
          <CreateDocumentLinkDialog
            scope="project"
            projectId={props.projectId}
            onClose={() => setCreateOpen(false)}
          />
        ) : (
          <CreateDocumentLinkDialog
            scope="submission"
            submissionId={props.submissionId}
            onClose={() => setCreateOpen(false)}
          />
        ))}
      {deletingFor && (
        <DeleteDocumentLinkDialog
          documentLinkId={deletingFor.id}
          displayLabel={deletingFor.display_name ?? deletingFor.link_role ?? null}
          onClose={() => setDeletingFor(null)}
        />
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// DocumentLinkRowItem — row enrichi avec bouton × destructive
// -----------------------------------------------------------------------------

type RowProps = {
  dl: DocumentLinkRow;
  onDelete: () => void;
};

function DocumentLinkRowItem({ dl, onDelete }: RowProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);

  const label = dl.display_name?.trim() || t("document_links.section.untitled");
  const role = dl.link_role?.trim() || null;

  // 9.T.5.v3.7 D-NEW-V36-B closure — bouton ouvrir le PDF dans un nouvel onglet
  // via URL signée `/v1/files/:id/download` (TTL 5 min, générée à la demande).
  // Le storage path brut n'est JAMAIS exposé au client — seule l'URL signée
  // Supabase transite. Erreur (fichier expiré, quota Storage, network) → toast.
  const downloadUrl = useFileDownloadUrl();

  const onOpenFile = () => {
    if (downloadUrl.isPending) return;
    downloadUrl.mutate(dl.file_id, {
      onSuccess: (data) => {
        // `noopener` + `noreferrer` : anti-tabnabbing standard.
        const win = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!win) {
          // popup blocker OR navigateur strict → toast pour signaler explicit.
          toast.error(t("document_links.section.download_open_failed"));
        }
      },
      onError: (err) => {
        const msg =
          err instanceof ApiException
            ? getApiErrorMessage(err, lang) || t("document_links.section.download_error")
            : t("document_links.section.download_error");
        toast.error(msg);
      },
    });
  };

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onOpenFile}
            disabled={downloadUrl.isPending}
            aria-label={t("document_links.section.open_file_aria", { label })}
            className="text-brand-700 decoration-brand-200 hover:text-brand-800 hover:decoration-brand-400 focus-visible:outline-brand-500 min-w-0 truncate rounded-sm text-left text-sm font-medium underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-progress disabled:text-slate-400"
          >
            {label}
            {downloadUrl.isPending && (
              <span
                className="ml-2 inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent align-middle motion-reduce:animate-none"
                aria-hidden="true"
              />
            )}
          </button>
          <AgentBadge displayName={dl.created_by_agent_display_name} variant="created" />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="truncate font-mono" title={dl.file_id}>
            {t("document_links.section.file_id_label")} {dl.file_id.slice(0, 8)}…
          </span>
          {role && (
            <>
              <span aria-hidden="true">·</span>
              <span>
                {t("document_links.section.role_label")} {role}
              </span>
            </>
          )}
          <span aria-hidden="true">·</span>
          <span>
            {t("document_links.section.linked_at_label")} {dl.created_at.slice(0, 10)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onDelete}
          aria-label={t("document_links.section.delete_aria", {
            label,
          })}
          className="rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700 shadow-sm hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
        >
          {t("document_links.section.delete_button")}
        </button>
      </div>
    </li>
  );
}
